import { modal, button, severityTicks, sheetActions } from "../../design/components.js";
import { cellToLatLng } from "h3-js";
import type { IncidentView } from "./incident-model.js";
import type { ReportInput } from "./report-publish.js";

export interface ReportSheetCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  /** Default report cell ("here") — the GPS fix or the chosen home/browse cell (res 8). */
  homeCell: string;
  placeName?: string;
  /** In-radius incidents (for corroborate mode); newest/closest first. */
  nearby: IncidentView[];
  /** Optional GPS resolver; returns a res-8 cell or null. Called on "use my location". */
  geolocate?: () => Promise<string | null>;
  /** Live res-8 cell ("current location"), when live-follow is active. */
  currentCell?: string;
  /** Home place name (shown when Home is the chosen report location). */
  homePlaceName?: string;
  /** Optional map pin-pick mount (WebGL-guarded by the caller). */
  mountPicker?: (container: HTMLElement, onPick: (cell: string) => void) => void;
  onSubmit: (input: ReportInput) => Promise<void>;
  onClose: () => void;
}

const HAZARDS = ["fire", "flood", "earthquake", "conflict", "other"] as const;

/** The report bottom sheet (GUI §6). Corroborate-first when a matching incident
 *  is nearby, else the three-decision flow (what → where → how bad). */
export function reportSheet(ctx: ReportSheetCtx): HTMLElement {
  const t = ctx.t;
  const body = document.createElement("div");
  body.className = "report-sheet__body";
  const sheet = modal({ title: t("report.title"), content: body, onClose: ctx.onClose, sheetId: "report" });

  // Draft state
  let hazard = "";
  let severity = 2;
  let note = "";

  // Default to the current (live/GPS) location when available, else home.
  const hasCurrent = !!ctx.currentCell || !!ctx.geolocate;
  let whereMode: "current" | "home" | "pin" = hasCurrent ? "current" : "home";
  let cell = ctx.currentCell ?? ctx.homeCell;
  let locating = false;
  let locateFailed = false;

  const render = (el: HTMLElement) => body.replaceChildren(el);

  async function submit(input: ReportInput) {
    try {
      await ctx.onSubmit(input);
      render(confirmation());
    } catch {
      // Re-render the current step with an error banner appended.
      const err = document.createElement("p");
      err.className = "bx-body report-sheet__error";
      err.textContent = t("report.error");
      body.append(err);
    }
  }

  function confirmation(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "report-sheet__done";
    wrap.setAttribute("data-step", "done");
    const h = document.createElement("h2");
    h.className = "bx-display";
    h.textContent = t("report.done.title");
    const p = document.createElement("p");
    p.className = "bx-body";
    p.textContent = t("report.done.body");
    const done = button({ label: t("report.done.close"), variant: "primary", onClick: ctx.onClose });
    done.setAttribute("data-action", "done-close");
    wrap.append(h, p, done);
    return wrap;
  }

  // ---- Corroborate mode ----
  function corroborate(match: IncidentView): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "corroborate");
    const q = document.createElement("p");
    q.className = "bx-body";
    q.textContent = t("report.corroborate.q", {
      hazard: t(`hazard.${match.hazard}`).toLowerCase(),
      place: ctx.placeName ?? t("now.place.default"),
    });
    const yes = button({
      label: t("report.corroborate.yes"),
      variant: "primary",
      // At the INCIDENT's cell — never the home/live cell (privacy + same cluster).
      onClick: () => void submit({ hazard: match.hazard, severity: 2, cell: match.cell, note: "" }),
    });
    yes.setAttribute("data-action", "corroborate-yes");
    const no = button({
      label: t("report.corroborate.no"),
      variant: "secondary",
      onClick: () => render(stepWhat()),
    });
    no.setAttribute("data-action", "corroborate-no");
    wrap.append(q, yes, no);
    return wrap;
  }

  // ---- Step 1: What ----
  function stepWhat(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "what");
    const h = document.createElement("h2");
    h.className = "bx-display";
    h.textContent = t("report.step.what");
    wrap.append(h);
    const grid = document.createElement("div");
    grid.className = "report-sheet__hazards";
    for (const hz of HAZARDS) {
      const b = button({
        label: t(`hazard.${hz}`),
        variant: hz === hazard ? "primary" : "secondary",
        onClick: () => {
          hazard = hz;
          render(stepWhat()); // reflect selection
        },
      });
      b.setAttribute("data-hazard", hz);
      grid.append(b);
    }
    wrap.append(grid);
    const next = button({ label: t("report.next"), variant: "primary", onClick: () => render(stepWhere()) });
    next.setAttribute("data-action", "next");
    (next as HTMLButtonElement).disabled = !hazard;
    wrap.append(sheetActions({ primary: next }));
    return wrap;
  }

  // ---- Step 2: Where (Current / Home / Pin chooser) ----
  function stepWhere(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "where");
    const h = document.createElement("h2");
    h.className = "bx-display";
    h.textContent = t("report.step.where");
    wrap.append(h);

    const honesty = document.createElement("p");
    honesty.className = "bx-microlabel";
    honesty.textContent = t("report.where.honesty");
    wrap.append(honesty);

    const modes = document.createElement("div");
    modes.className = "report-sheet__where-modes";
    const selectedLine = document.createElement("p");
    selectedLine.className = "bx-microlabel report-sheet__where-selected";

    const showSelected = () => {
      if (whereMode === "home") {
        selectedLine.textContent = `${t("report.where.selected")}: ${ctx.homePlaceName ?? t("now.place.default")}`;
      } else if (whereMode === "current" && locating) {
        selectedLine.textContent = t("report.where.locating");
      } else {
        const c = cell;
        const [la, lo] = c ? cellToLatLng(c) : [0, 0];
        selectedLine.textContent = c ? `${t("report.where.selected")}: ${la.toFixed(4)}, ${lo.toFixed(4)}` : t("report.where.locating");
      }
    };

    const pickerWrap = document.createElement("div");
    const renderPicker = () => {
      pickerWrap.replaceChildren();
      if (whereMode === "pin" && ctx.mountPicker) {
        const pick = document.createElement("div");
        pick.className = "report-sheet__picker";
        pick.setAttribute("data-picker", "");
        ctx.mountPicker(pick, (c) => { cell = c; showSelected(); });
        const hint = document.createElement("p");
        hint.className = "bx-microlabel report-sheet__pickhint";
        hint.textContent = t("report.where.pickHint");
        pickerWrap.append(pick, hint);
      }
    };

    const setMode = (m: "current" | "home" | "pin") => {
      whereMode = m;
      if (m === "home") { cell = ctx.homeCell; locating = false; locateFailed = false; }
      if (m === "current") {
        if (ctx.currentCell) { cell = ctx.currentCell; locating = false; locateFailed = false; }
        else if (ctx.geolocate) {
          locating = true;
          render(stepWhere());                       // highlight Current + show "Locating…"
          void ctx.geolocate().then((c) => {
            locating = false;
            if (c) { cell = c; locateFailed = false; }
            else { whereMode = "home"; cell = ctx.homeCell; locateFailed = true; }
            render(stepWhere());
          });
          return;
        }
      }
      if (m === "pin") { locateFailed = false; }
      render(stepWhere()); // reflect selection (re-reads whereMode/cell)
    };

    const modeBtn = (m: "current" | "home" | "pin", key: string) => {
      const b = button({ label: t(key), variant: m === whereMode ? "primary" : "secondary", onClick: () => setMode(m) });
      b.setAttribute("data-where-mode", m);
      b.setAttribute("aria-pressed", String(m === whereMode));
      return b;
    };

    if (hasCurrent) modes.append(modeBtn("current", "report.where.current"));
    modes.append(modeBtn("home", "report.where.home"));
    if (ctx.mountPicker) modes.append(modeBtn("pin", "report.where.pin"));
    wrap.append(modes, selectedLine, pickerWrap);
    showSelected();
    renderPicker();

    if (locateFailed) {
      const n = document.createElement("p");
      n.className = "bx-microlabel report-sheet__locate-failed";
      n.setAttribute("data-locate-failed", "");
      n.textContent = t("report.where.locateFailed");
      wrap.append(n);
    }

    const back = button({ label: t("report.back"), variant: "secondary", onClick: () => render(stepWhat()) });
    back.setAttribute("data-action", "back");
    const next = button({ label: t("report.next"), variant: "primary", onClick: () => render(stepHowBad()) });
    next.setAttribute("data-action", "next");
    wrap.append(sheetActions({ back, primary: next }));
    return wrap;
  }

  // ---- Step 3: How bad ----
  function stepHowBad(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("data-step", "howbad");
    const h = document.createElement("h2");
    h.className = "bx-display";
    h.textContent = t("report.step.howbad");
    wrap.append(h);

    const ticks = document.createElement("div");
    ticks.className = "report-sheet__severity";
    const sevLabel = document.createElement("div");
    sevLabel.className = "bx-microlabel";
    sevLabel.textContent = t("report.severity.label");
    ticks.append(sevLabel);
    for (let s = 1; s <= 3; s++) {
      const b = button({
        label: String(s),
        variant: s === severity ? "primary" : "secondary",
        onClick: () => { severity = s; render(stepHowBad()); },
      });
      b.setAttribute("data-sev", String(s));
      ticks.append(b);
    }
    ticks.append(severityTicks(severity, 3));
    wrap.append(ticks);

    const noteInput = document.createElement("input");
    noteInput.type = "text";
    noteInput.maxLength = 120;
    noteInput.placeholder = t("report.note.placeholder");
    noteInput.setAttribute("data-note", "");
    noteInput.value = note;
    noteInput.addEventListener("input", () => { note = noteInput.value; });
    wrap.append(noteInput);

    const back = button({ label: t("report.back"), variant: "secondary", onClick: () => render(stepWhere()) });
    back.setAttribute("data-action", "back");
    const send = button({
      label: t("report.submit"),
      variant: "primary",
      onClick: () => void submit({ hazard, severity, cell, note }),
    });
    send.setAttribute("data-action", "submit");
    wrap.append(sheetActions({ back, primary: send }));
    return wrap;
  }

  // Entry: corroborate if a matching incident WITH a cell is nearby, else the full flow
  // (an incident without a cell has nothing to corroborate at; no home-cell fallback).
  const match = ctx.nearby.find((i) => !!i.cell);
  render(match ? corroborate(match) : stepWhat());
  return sheet;
}
